import { Professional } from '@/hooks/useSearch';
import { getNormalizedAddressParts } from '@/lib/locationGrouping';

function normalizeNamePart(value: string): string {
  return value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/^(dr\.?|dra\.?)\s+/i, '')
    .replace(/\s+/g, ' ')
    .trim()
    .toUpperCase();
}

function buildDedupeKey(professional: Professional): string {
  const { calle, numero, localidad } = getNormalizedAddressParts(professional);
  const apellido = normalizeNamePart(professional.APELLIDO || '');
  const nombre = normalizeNamePart(professional.NOMBRE || '');

  return `${apellido}|${nombre}|${calle}|${numero}|${localidad}`;
}

function mergeSpecialties(existing: Professional, incoming: Professional): void {
  const specialties = new Set(
    `${existing.ESPECIALIDAD}, ${incoming.ESPECIALIDAD}`
      .split(',')
      .map((specialty) => specialty.trim())
      .filter(Boolean)
  );
  existing.ESPECIALIDAD = Array.from(specialties).join(', ');
}

function hasText(value: unknown): boolean {
  if (value == null) return false;
  return String(value).trim().length > 0;
}

function preferRicherRecord(existing: Professional, incoming: Professional): Professional {
  const existingScore =
    Number(hasText(existing.TELEFONO)) +
    Number(Boolean(existing.LOCATION_LAT && existing.LOCATION_LONG)) +
    Number(hasText(existing.OBSERVACIONES));

  const incomingScore =
    Number(hasText(incoming.TELEFONO)) +
    Number(Boolean(incoming.LOCATION_LAT && incoming.LOCATION_LONG)) +
    Number(hasText(incoming.OBSERVACIONES));

  return incomingScore > existingScore ? { ...incoming } : existing;
}

/**
 * La API puede devolver:
 * - un registro por sub-especialidad para el mismo prestador+domicilio
 * - duplicados del mismo prestador+domicilio con distinto ID_PRESTADOR/DOMICONS_ID
 * Deduplicamos por nombre + domicilio normalizado y fusionamos especialidades.
 */
export function dedupeProfessionals(professionals: Professional[]): Professional[] {
  return Array.from(
    professionals.reduce((acc, professional) => {
      const key = buildDedupeKey(professional);
      const existing = acc.get(key);

      if (!existing) {
        acc.set(key, { ...professional });
        return acc;
      }

      mergeSpecialties(existing, professional);
      const preferred = preferRicherRecord(existing, professional);
      acc.set(key, { ...preferred, ESPECIALIDAD: existing.ESPECIALIDAD });
      return acc;
    }, new Map<string, Professional>()).values()
  );
}
